import React, { useRef, useEffect } from 'react';
import { Train, X } from 'lucide-react';
import { getDelayMinutes, getTrainStatusAndPosition } from '../../../utils/trackingHelpers';

const TrainTrackingResults = ({ trainData, trainNumber, onClose }) => {
  const currentStationRef = useRef(null);

  // Auto-scroll effect
  useEffect(() => {
    if (trainData && currentStationRef.current) {
      setTimeout(() => {
        currentStationRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 800);
    }
  }, [trainData]);

  const trainPosition = React.useMemo(() => {
    if (!trainData) return null;
    return getTrainStatusAndPosition(trainData.last_update, trainData.stations, trainData.current_station_code);
  }, [trainData]);

  if (!trainData) return null;

  return (
    <div className="w-full bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-6 border border-slate-100 shadow-sm transition-all duration-300 relative overflow-hidden animate-fade-in-up">
      {/* Minimal Accent Bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-[#067978]" />

      {/* Header: Train Name, Number, Status Pill, Close Button */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 pb-5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Train size={20} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg sm:text-xl font-normal text-slate-800 tracking-tight uppercase">
                {trainData.train_name}
              </h2>
              <span className="text-[11px] font-mono font-normal px-2 py-0.5 rounded-md bg-slate-50 text-slate-600 border border-slate-200/60 uppercase">
                #{trainNumber}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-400 font-normal">
              <span>{trainData.last_update?.split('Upcoming')[0]}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-center">
          {trainPosition?.message && (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-normal bg-emerald-50 text-emerald-700 border border-emerald-200/80 shadow-2xs">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span>{trainPosition.message}</span>
            </div>
          )}

          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer"
              title="Close tracking and return to list"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Train Route Stations Timeline */}
      <div className="bg-slate-50/50 rounded-2xl p-4 sm:p-5 border border-slate-100 shadow-2xs mt-5 relative overflow-hidden">
        <div className="absolute left-[50%] top-6 bottom-6 w-0.5 bg-slate-200/70 -translate-x-1/2 z-0 hidden md:block" />

        <div className="relative z-10 space-y-0">
          {trainData.stations?.map((station, index) => {
            const activeIdx = trainPosition?.currentStationIdx ?? -1;
            const isTransit = trainPosition?.isInTransit;
            const isPassed = index < activeIdx;
            const isCurrentActiveRow = index === activeIdx;
            const isHighlightedRow = isCurrentActiveRow;
            const delayArr = getDelayMinutes(station.arrival_time, station.est_arrival_time);

            return (
              <div
                key={index}
                ref={isHighlightedRow ? currentStationRef : null}
                className={`grid grid-cols-[1fr_3rem_1fr] gap-2 md:gap-4 group min-h-[3.5rem] transition-all duration-300 rounded-xl ${
                  isHighlightedRow
                    ? 'bg-primary/5 border border-primary/15 z-10 my-1 py-1.5'
                    : 'hover:bg-slate-100/50'
                }`}
              >
                <div className="text-right py-2 flex flex-col justify-center">
                  <h3
                    className={`font-normal text-sm leading-tight ${
                      isHighlightedRow
                        ? 'text-primary font-medium'
                        : isPassed
                        ? 'text-slate-700'
                        : 'text-slate-400'
                    }`}
                  >
                    {station.station_name}
                  </h3>
                  <p className="text-[10px] font-normal text-slate-400">{station.station_code}</p>
                </div>

                <div className="relative flex flex-col items-center">
                  {index !== trainData.stations.length - 1 && (
                    <div
                      className={`absolute top-1/2 left-1/2 -translate-x-1/2 w-0.5 h-[100%] -z-10 ${
                        isPassed || (isCurrentActiveRow && isTransit) ? 'bg-primary' : 'bg-transparent'
                      }`}
                    ></div>
                  )}
                  {index > 0 && (
                    <div
                      className={`absolute top-0 bottom-1/2 left-1/2 -translate-x-1/2 w-0.5 -z-10 ${
                        isPassed || isCurrentActiveRow ? 'bg-primary' : 'bg-transparent'
                      }`}
                    ></div>
                  )}
                  <div className="relative z-10 my-auto">
                    {isCurrentActiveRow && !isTransit ? (
                      <div className="w-8 h-8 bg-white border-2 border-primary rounded-full flex items-center justify-center shadow-md relative z-20">
                        <div className="absolute inset-0 bg-primary/20 rounded-full animate-ping"></div>
                        <Train className="text-primary w-4 h-4 relative z-10" />
                      </div>
                    ) : isCurrentActiveRow && isTransit ? (
                      <div className="w-4 h-4 bg-primary border-2 border-white rounded-full shadow-sm ring-2 ring-primary/20"></div>
                    ) : isPassed ? (
                      <div className="w-3 h-3 bg-primary border-2 border-white rounded-full shadow-sm"></div>
                    ) : (
                      <div className="w-2.5 h-2.5 bg-white border-2 border-slate-300 rounded-full"></div>
                    )}
                  </div>
                  {isTransit && isCurrentActiveRow && (
                    <div className="absolute -bottom-[1.5rem] left-1/2 -translate-x-1/2 z-30 pointer-events-none">
                      <div className="flex flex-col items-center">
                        <div className="w-10 h-10 bg-white rounded-full border-2 border-primary shadow-lg flex items-center justify-center animate-bounce-slow">
                          <Train className="text-primary w-5 h-5" fill="currentColor" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="text-left py-2 flex flex-col justify-center pl-2">
                  <div className="grid grid-cols-2 gap-x-4">
                    <div>
                      <p className="text-[9px] uppercase font-normal text-slate-400 tracking-wider">Arr</p>
                      {station.est_arrival_time && station.est_arrival_time !== station.arrival_time ? (
                        <div className="flex flex-col">
                          <span
                            className={`font-mono font-normal text-xs ${
                              isHighlightedRow ? 'text-primary font-medium' : 'text-slate-800'
                            }`}
                          >
                            {station.est_arrival_time}
                          </span>
                          <span className="text-[9px] text-slate-400 font-normal">Sch: {station.arrival_time}</span>
                        </div>
                      ) : (
                        <p
                          className={`font-mono text-xs font-normal ${
                            isHighlightedRow ? 'text-primary font-medium' : 'text-slate-700'
                          }`}
                        >
                          {station.arrival_time}
                        </p>
                      )}
                      {delayArr && (
                        <span className={`text-[9px] font-normal block ${delayArr.color}`}>{delayArr.label}</span>
                      )}
                    </div>
                    <div>
                      <p className="text-[9px] uppercase font-normal text-slate-400 tracking-wider">Dep</p>
                      {station.est_departure_time && station.est_departure_time !== station.departure_time ? (
                        <div className="flex flex-col">
                          <span
                            className={`font-mono font-normal text-xs ${
                              isHighlightedRow ? 'text-primary font-medium' : 'text-slate-800'
                            }`}
                          >
                            {station.est_departure_time}
                          </span>
                          <span className="text-[9px] text-slate-400 font-normal">Sch: {station.departure_time}</span>
                        </div>
                      ) : (
                        <p
                          className={`font-mono text-xs font-normal ${
                            isHighlightedRow ? 'text-primary font-medium' : 'text-slate-700'
                          }`}
                        >
                          {station.departure_time}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TrainTrackingResults;
